import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { addKeyValueRow, type KeyValueRow, type KeyValueRules, type RowsResult } from './propertyDrafts';
import { PropertiesConflictNotice } from './PropertiesConflictNotice';

type Props = {
  title: string;
  rows: KeyValueRow[];
  rules: KeyValueRules;
  validity: RowsResult;
  onChange: (rows: KeyValueRow[]) => void;
  onAdd?: () => void;
  onRemove?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  saveDisabled?: boolean;
  onSave: () => void;
  message?: string;
  /** F20: `reloading` turns the notice's Reload off while a reload runs (every editor is locked meanwhile). */
  conflict?: { message: string; onReload: () => void; reloading?: boolean };
  /** The item also holds a "__proto__" key the window never receives: shown as a read-only row with the reason. */
  uncarriedNotice?: string;
  testId: string;
};

/** Rows keep stable ids; an invalid set (empty or duplicate key) is shown on the rows and never becomes a map (review C F3). */
export function PropertiesKeyValueEditor(props: Props) {
  const { title, rows, rules, validity, onChange, disabled, disabledReason, saveDisabled, onSave, message, conflict, testId } = props;
  const invalidRows = new Set(validity.ok ? [] : validity.rowIds);
  const update = (id: string, patch: Partial<KeyValueRow>) => onChange(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  return (
    <section className="space-y-2" data-testid={`${testId}-section`}>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{title}</h3>
        <Button type="button" size="xs" variant="outline" className="gap-1" disabled={disabled} title={disabledReason} data-testid={`${testId}-add`} onClick={() => { onChange(addKeyValueRow(rows, rules)); props.onAdd?.(); }}><Plus className="h-3 w-3" aria-hidden="true" />Add</Button>
      </div>
      {rows.length === 0 && !props.uncarriedNotice && <p className="text-xs text-muted-foreground">No values</p>}
      {props.uncarriedNotice && <p className="text-xs text-muted-foreground"><span className="font-mono">__proto__ (not shown)</span> <span>{props.uncarriedNotice}</span></p>}
      {/* U18: a two-column table — Key / Value headers, the value wider, each full value in its tooltip, a trash button per row. */}
      {rows.length > 0 && <div className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_1.75rem] gap-1 text-[11px] font-medium text-muted-foreground" aria-hidden="true"><span>Key</span><span>Value</span><span /></div>}
      {rows.map((row) => (
        <div key={row.id} className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_1.75rem] items-start gap-1">
          <Input aria-label={`${title} key`} aria-invalid={invalidRows.has(row.id) || undefined} className={`h-7 font-mono text-xs ${invalidRows.has(row.id) ? 'border-destructive' : ''}`} spellCheck={false} value={row.key} disabled={disabled} title={disabledReason ?? row.key} onChange={(event) => update(row.id, { key: event.target.value })} />
          {/* U18 remainder (C1): a long value wraps and grows (field-sizing: content) instead of being clipped behind a hover title.
              Z8 (last UI/UX pass): at spaces — only a token longer than the field breaks (`break-all` split "o / nly"). */}
          <Textarea rows={1} aria-label={`${title} value`} className="field-sizing-content min-h-7 resize-none px-3 py-1 text-xs [overflow-wrap:anywhere]" spellCheck={false} value={row.value} disabled={disabled} title={disabledReason ?? (row.value || undefined)} onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); /* a header value is one line */ }} onChange={(event) => update(row.id, { value: event.target.value })} />
          <Button type="button" size="icon" variant="ghost" className="size-7" disabled={disabled} title={disabledReason ?? `Remove ${row.key || 'this row'}`} onClick={() => { onChange(rows.filter((candidate) => candidate.id !== row.id)); props.onRemove?.(); }}>
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /><span className="sr-only">Remove</span>
          </Button>
        </div>
      ))}
      {!validity.ok && <p className="text-xs text-destructive">{validity.message}</p>}
      {conflict && <PropertiesConflictNotice message={conflict.message} onReload={conflict.onReload} reloading={conflict.reloading} testId={testId} />}
      <div className="flex items-center gap-2">
        <Button type="button" size="xs" disabled={disabled || saveDisabled || !validity.ok || !!conflict} title={disabledReason} onClick={onSave} data-testid={`${testId}-save`}>Save</Button>
        {message && <span className="text-xs text-muted-foreground">{message}</span>}
      </div>
    </section>
  );
}
